import { FC, ReactNode } from 'react';
import { Box, useTheme } from '@mui/material';
import { Outlet, useLocation } from 'react-router-dom';

import Sidebar from './Sidebar';
import Header from './Header';
import { sidebarLayoutSx } from 'src/common/CommomSxs';
import { RootState } from 'src/redux/store';
import { useSelector } from 'react-redux';
import { paths } from 'src/common/config/TextMessage';
import {
  isAiAddonLocked,
  isLocalSeoUser
} from 'src/common/config/FunctionHelper';
import SubscriptionPaymentFailed from 'src/common/SubscriptionPaymentFailed';
// import SiteDeletedWarning from 'src/common/SiteDeletedWarning';

interface SidebarLayoutProps {
  children?: ReactNode;
}

/**
 * SidebarLayout is a functional component for rendering a layout with a sidebar.
 *
 * @param {SidebarLayoutProps} props - The properties and children to render within the layout.
 * @returns {ReactElement} The SidebarLayout component.
 */
const SidebarLayout: FC<SidebarLayoutProps> = () => {
  const theme = useTheme();
  const isLocalSeo = isLocalSeoUser();
  let isPremiumUser = useSelector((state: RootState) => state.user.premiumUser);
  const stripeLoader =
    useSelector((state: RootState) => state.siteAudit.stripeLoader) ?? true;
  let userDetails: any =
    useSelector((state: RootState) => state.counter.userDetails) ?? {};
  // Check if the site audit loader is active
  const loader =
    useSelector((state: RootState) => state.siteAudit.loader) ?? true;
  let stripeSubscriptionDetails = useSelector(
    (state: RootState) => state.user.subscription
  );
  let planDetails = useSelector((state: RootState) => state.user.planDetails);
  let activePlanName = useSelector((state: RootState) => state.user.activePlan);
  const isNotUnlocked = isAiAddonLocked();
  const isFreePlan = activePlanName === planDetails.free || isLocalSeo;
  const location = useLocation();
  const isNoPlanAccount =
    !stripeLoader &&
    !loader &&
    userDetails?.email &&
    userDetails?.temp == true &&
    (!stripeSubscriptionDetails?.subscription_id || !isPremiumUser);
  const isExpireAccount =
    !stripeLoader &&
    !loader &&
    !isFreePlan &&
    userDetails?.email &&
    userDetails?.temp !== true &&
    (!stripeSubscriptionDetails?.subscription_id || !isPremiumUser);
  const isPause =
    stripeSubscriptionDetails &&
    stripeSubscriptionDetails?.pause_collection?.behavior;

  const isNoPlanOrExpire = isNoPlanAccount || isExpireAccount;
  const isNoPlanOrPause = isNoPlanOrExpire || isPause;
  const pathArray = [paths?.dashboard, paths?.billing];
  const includePath = pathArray.includes(location.pathname);
  const matchPath = isNoPlanOrPause && includePath;
  const urls = isNotUnlocked
    ? [
        paths.dashboard,
        paths.keywordResearch,
        paths.orbitAiAgent,
        paths.siteAudit,
        paths.backlinkPage,
        paths.otherTools
      ]
    : [paths.keywordResearch, paths.orbitAiAgent, paths.backlinkPage, paths.otherTools];
  const isMatchingPath = urls.includes(location.pathname);
  const isFreeAndPath = isFreePlan && isMatchingPath;
  const stripeExpired = stripeSubscriptionDetails?.expired && includePath;

  return (
    <>
      <Box sx={sidebarLayoutSx.sidebarmainbox(theme)}>
        <Header />
        <Sidebar />
        <Box
          sx={{
            ...sidebarLayoutSx.containerBox(theme),
            pt:
              isFreeAndPath || matchPath || stripeExpired
                ? 'calc(42px + 1.25rem)'
                : 'calc(63px + 1.25rem)',
            [theme.breakpoints.up('lg')]: {
              ml: isFreeAndPath || matchPath || stripeExpired ? 31.3 : 31.5
            }
          }}
        >
          <Box
            display="block"
            style={sidebarLayoutSx.mainContainerStyle}
            className={
              isFreeAndPath || matchPath || stripeExpired ? '' : 'px-5'
            }
          >
            <SubscriptionPaymentFailed />
            {/* <SiteDeletedWarning /> */}
            <Outlet />
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default SidebarLayout;
